// SPDX-License-Identifier: AGPL-3.0-or-later

import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Button, Renderer, type DraftAnswers } from '@intakeform/ui';
import { useQuestionnaire } from '../../api/questionnaires.js';
import {
  useAutofillJob,
  useAutofillJobEmrLink,
  usePatchAutofillJob,
  useUnlinkPatientEmr,
} from '../../api/autofill.js';
import { buildQuestionnaireResponse } from '../../stores/intake.js';
import { qrToDraftAnswers, statusLabel } from './helpers.js';

interface AutofillReviewRouteProps {
  /**
   * Which transition the primary button fires when clicked. Drives the
   * label + which role the back-end requires. Used by the admin preview
   * page and the doctor approve page; the underlying rendering is the
   * same.
   */
  stage: 'admin' | 'doctor';
}

/**
 * Shared rendering for both the admin preview and the doctor approval
 * page. The form is editable in both stages (per icj decision —
 * overwrite-only, no per-field diff tracking).
 */
export function AutofillReviewRoute({ stage }: AutofillReviewRouteProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const job = useAutofillJob(id);
  const emrLink = useAutofillJobEmrLink(id);
  const unlink = useUnlinkPatientEmr(id);
  const form = useQuestionnaire(job.data?.questionnaireId);
  const patch = usePatchAutofillJob();

  const [answers, setAnswers] = useState<DraftAnswers | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  // Hydrate the local answer state from the persisted draft once.
  useMemo(() => {
    if (job.data !== undefined && answers === null) {
      setAnswers(qrToDraftAnswers(job.data.draftQr));
    }
  }, [job.data, answers]);

  if (job.isLoading) return <p className="muted">Loading autofill job…</p>;
  if (job.error || !job.data)
    return <div className="error-banner">Failed to load autofill job.</div>;
  if (form.isLoading) return <p className="muted">Loading form…</p>;
  if (form.error || !form.data) return <div className="error-banner">Failed to load form.</div>;

  const j = job.data;
  const stageReady =
    (stage === 'admin' && j.status === 'pending_admin_review') ||
    (stage === 'doctor' && j.status === 'pending_doctor_approval');

  const primaryAction = stage === 'admin' ? 'admin_approve' : 'doctor_approve';
  const primaryLabel = stage === 'admin' ? 'Send for doctor approval' : 'Approve & publish';

  const handlePrimary = async (): Promise<void> => {
    if (!j || !answers) return;
    const draftQr = buildQuestionnaireResponse({
      questionnaireRef: `Questionnaire/${form.data.resource.id ?? j.questionnaireId}|${form.data.resource.version ?? '1'}`,
      status: 'in-progress',
      answers,
      questionnaire: form.data.resource,
    });
    try {
      const updated = await patch.mutateAsync({
        id: j.id,
        draftQr,
        transition: primaryAction,
      });
      if (primaryAction === 'doctor_approve') {
        navigate(`/autofill/${updated.id}/print`);
      } else {
        navigate('/autofill');
      }
    } catch {
      // surfaced via patch.error
    }
  };

  const handleReject = async (): Promise<void> => {
    if (!j) return;
    try {
      await patch.mutateAsync({
        id: j.id,
        transition: 'reject',
        reason: rejectReason.trim() === '' ? 'No reason given.' : rejectReason.trim(),
      });
      navigate('/autofill');
    } catch {
      // surfaced via patch.error
    }
  };

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-bold tracking-tight">
            {stage === 'admin' ? 'Admin review' : 'Doctor approval'}
          </h1>
          <Link to="/autofill" className="text-sm text-primary-700">
            ← Back to autofill jobs
          </Link>
        </div>
        <p className="text-sm text-muted-foreground">
          Form: <strong>{form.data.resource.title}</strong> · Patient:{' '}
          <strong>{j.emrContextSnapshot.patient.displayName}</strong> · Status:{' '}
          <span data-testid="autofill-status">{statusLabel(j.status)}</span>
        </p>
      </header>

      {/*
        Where the prefilled data came from (intakeform-preview-200.6). Shown
        unconditionally, because the point is that a clinician can NOTICE when
        the source record is the wrong person — a link nobody can see is a link
        nobody can audit. Unlinking is offered right here for the same reason:
        the moment you spot a bad correlation is the moment you should be able
        to break it.
      */}
      <section className="emr-provenance" aria-labelledby="emr-provenance-heading">
        <h2 id="emr-provenance-heading" className="emr-provenance-title">
          Prefilled from
        </h2>
        <p className="emr-provenance-body">
          <strong>{j.emrAdapterId}</strong> record{' '}
          <code data-testid="emr-record-ref">{j.patientRef}</code>
        </p>
        {emrLink.data ? (
          <>
            <p className="emr-provenance-note">
              This patient is linked to that record, so future prefills will not ask you to pick
              again.{' '}
              {emrLink.data.confirmedAt === null ? (
                <strong>This link has not been confirmed by a person.</strong>
              ) : null}
            </p>
            <button
              data-ui-button=""
              data-variant="secondary"
              type="button"
              disabled={unlink.isPending}
              onClick={() => {
                if (emrLink.data) unlink.mutate(emrLink.data.id);
              }}
            >
              {unlink.isPending ? 'Unlinking…' : 'Unlink this patient'}
            </button>
            {unlink.error ? (
              <div className="error-banner" role="alert">
                Could not unlink: {unlink.error.message}
              </div>
            ) : null}
          </>
        ) : (
          <p className="emr-provenance-note">
            This patient is not linked to an EMR record, so each prefill asks you to pick one.
          </p>
        )}
      </section>

      {!stageReady ? (
        <div className="error-banner" role="alert">
          This job is in status "{statusLabel(j.status)}" and cannot be{' '}
          {stage === 'admin' ? 'reviewed by admin' : 'approved by doctor'} from here.
        </div>
      ) : null}

      {j.prefillFindings.length > 0 ? (
        <div
          className="border border-warning-300 dark:border-warning-700 bg-warning-50/40 dark:bg-warning-900/20 rounded-md p-3"
          data-testid="autofill-unmapped-list"
        >
          <p className="font-semibold mb-2">{j.prefillFindings.length} field(s) need your input</p>
          <ul className="list-disc pl-5 text-sm">
            {j.prefillFindings.map((f, i) => (
              <li key={`${String(f.linkId ?? 'form')}-${String(i)}`}>{f.message}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {answers !== null ? (
        <Renderer
          questionnaire={form.data.resource}
          answers={answers}
          onChange={(linkId, value) => setAnswers((a) => ({ ...(a ?? {}), [linkId]: value }))}
          disabled={!stageReady}
        />
      ) : null}

      {stageReady ? (
        <section className="space-y-3 border-t border-border pt-4">
          <div className="flex items-center gap-3">
            <Button
              type="button"
              onClick={() => {
                void handlePrimary();
              }}
              disabled={patch.isPending}
              data-testid={`autofill-${stage}-approve`}
            >
              {patch.isPending ? 'Saving…' : primaryLabel}
            </Button>
            {patch.error ? (
              <span className="error-banner" role="alert">
                {patch.error.message}
              </span>
            ) : null}
          </div>
          <div className="flex items-center gap-3">
            <input
              type="text"
              placeholder="Rejection reason…"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.currentTarget.value)}
              className="flex-1 max-w-md rounded-md border border-border bg-surface p-2"
            />
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                void handleReject();
              }}
              disabled={patch.isPending}
              data-testid={`autofill-${stage}-reject`}
            >
              Reject
            </Button>
          </div>
        </section>
      ) : null}
    </div>
  );
}
